import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getApiUrl } from "../../api";

export default function FindId() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [nameMsg, setNameMsg] = useState("");
  const [phoneMsg, setPhoneMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [foundId, setFoundId] = useState<string | null>(null);
  const navigate = useNavigate();

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    borderColor: hasError ? "#ef4444" : "#e5e7eb",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNameMsg("");
    setPhoneMsg("");
    setFoundId(null);

    let hasError = false;
    if (!name.trim()) { setNameMsg("이름을 입력해주세요."); hasError = true; }
    if (!phone.trim()) { setPhoneMsg("휴대폰 번호를 입력해주세요."); hasError = true; }
    if (hasError) return;

    setLoading(true);
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/find_id"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });

      const data = await res.json();

      if (res.ok) {
        setFoundId(data.login_id);
      } else if (res.status === 404) {
        setNameMsg("입력하신 정보와 일치하는 회원이 없습니다.");
      } else {
        setNameMsg(data.detail || "오류가 발생했습니다.");
      }
    } catch (err) {
      setNameMsg("서버 연결 오류: " + (err as Error).message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#f5f2f0]">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_8px_32px_rgba(136,114,103,0.1)] px-9 py-10">

        {/* 헤더 */}
        <div className="mb-7">
          <h2 className="text-[20px] font-bold text-[#3d2f2a] mb-1">아이디 찾기</h2>
          <p className="text-[13px] text-[#999]">가입 시 등록한 이름과 휴대폰 번호를 입력해주세요.</p>
        </div>

        {!foundId ? (
          <form onSubmit={handleSubmit} className="flex flex-col">

            {/* 이름 */}
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
                이름
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="이름을 입력하세요"
                className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!nameMsg)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = nameMsg ? "#ef4444" : "#e5e7eb")}
              />
              {nameMsg && <span className="text-[12px] text-red-500 mt-1 block">{nameMsg}</span>}
            </div>

            {/* 휴대폰 번호 */}
            <div className="mb-6">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
                휴대폰 번호
              </label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="휴대폰 번호를 입력하세요 (예: 01012345678)"
                className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!phoneMsg)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = phoneMsg ? "#ef4444" : "#e5e7eb")}
              />
              {phoneMsg && <span className="text-[12px] text-red-500 mt-1 block">{phoneMsg}</span>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={[
                "w-full h-[46px] text-white border-none rounded-lg text-[15px] font-bold transition-colors duration-150",
                loading ? "bg-[#b8a39b] cursor-not-allowed" : "bg-[#887267] cursor-pointer hover:bg-[#6b5248]",
              ].join(" ")}
            >
              {loading ? "조회 중..." : "아이디 찾기"}
            </button>

          </form>
        ) : (
          /* 결과 화면 */
          <div className="flex flex-col items-center">
            <div className="w-full bg-[#f9f6f4] rounded-xl px-5 py-5 mb-6 text-center">
              <p className="text-[13px] text-[#999] mb-2">회원님의 아이디</p>
              <p className="text-[20px] font-bold text-[#3d2f2a] tracking-wide">{foundId}</p>
            </div>
            <button
              onClick={() => navigate("/auth/login")}
              className="w-full h-[46px] bg-[#887267] text-white border-none rounded-lg text-[15px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150 mb-3"
            >
              로그인하러 가기
            </button>
            <button
              onClick={() => { setFoundId(null); setName(""); setPhone(""); }}
              className="w-full h-[46px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-lg text-[15px] font-bold cursor-pointer hover:bg-[#f9f6f4] transition-colors duration-150"
            >
              다시 찾기
            </button>
          </div>
        )}

        {/* 하단 링크 */}
        <div className="flex justify-center items-center mt-5 gap-1">
          <Link to="/auth/login" className="px-3 text-[13px] text-[#666] no-underline">로그인</Link>
          <span className="text-gray-200">|</span>
          <Link to="/auth/find_pw" className="px-3 text-[13px] text-[#666] no-underline">비밀번호 찾기</Link>
          <span className="text-gray-200">|</span>
          <Link to="/auth/register" className="px-3 text-[13px] text-[#887267] font-semibold no-underline">회원가입</Link>
        </div>
      </div>
    </div>
  );
}
